import {
  ComponentHealthCondition,
  DEFAULT_PLAN_KEY,
  getComponentHealthCondition,
  getHealthPlanActionData,
  getMappedCriticality,
  getRiskScoreFromRiskClass,
  getRiskScoreHealthCondition,
  isErrorInfo,
} from 'features/fde'
import { isEmpty, isNaN, isNil } from 'lodash'
import { useMemo } from 'react'
import {
  AhpAction,
  AhpPlan,
  DamageCategory,
  DamageInfo,
  FDECustomerUnit,
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoComponentDefaults,
  GeckoEntry,
  GeckoEntryBase,
  GeckoInspection,
  GeckoMarkupLayer,
  GeckoPublishingState,
  GeckoRiskAssessment,
  GeckoSiteConfig,
  GeckoUnit,
  RolodexEntryType,
} from 'types'
import { compareOptionalNumbers, SortingPlacement } from 'utils/sortingHelpers'

import { useFetchBulkHealthData } from './api/fetchHealthData'
import {
  NeededDataFetch,
  useFetchNeededSiteDataFromRolodex,
} from './api/fetchRolodexData'
import { listYearsToSimTo } from './utils/numYearsAbleToSimTo'

export type AhpPlanActionAndCost = {
  plan: GeckoEntryBase<AhpPlan>
  action: GeckoEntryBase<AhpAction>
  cost: number | null
}

export enum NoRLHealthScore {
  NoInspection = 'HealthScore:NoInspection',
  NotEnoughData = 'HealthScore:NotEnoughData',
}
export type SiteViewHealthScore = ComponentHealthCondition | NoRLHealthScore
export type AggregateUnitData = {
  unitSpecs: {
    unitData: GeckoUnit
    assetModel: GeckoAssetModel | undefined
    childComponents: ReadonlyArray<GeckoComponent>
    mappedCriticality: string
  }
  mostRecentInspectionData: {
    inspection: GeckoInspection | undefined
    relevantCDF: GeckoComponentDataFrame | undefined
    relevantColorThreshold: GeckoColorScaleThreshold | undefined
  }
  health: {
    [year: number]:
      | undefined
      | {
          riskScore: number | undefined
          minRLFromYear: number | undefined
          percentBelowTMin: number | undefined
          damageAreas:
            | undefined
            | {
                [componentId: string]: {
                  damageAreas: DamageCategory[] | undefined
                  riskScore: number | undefined
                  healthScore: number | undefined
                }
              }
        }
  }
  allPlanCosts: ReadonlyArray<AhpPlanActionAndCost>
}

export type AggregateDataFetch =
  | {
      isMainLoading: false
      aggregateSiteData: ReadonlyArray<AggregateUnitData>
      isRlCalcLoading: boolean
    }
  | {
      isMainLoading: true
      aggregateSiteData: undefined
      isRlCalcLoading: boolean
    }

/**
 * Aggregates all the data needed for the SiteView page.
 */
export function useAggregateSiteData(siteID: string): AggregateDataFetch {
  const rolodexFetch: NeededDataFetch =
    useFetchNeededSiteDataFromRolodex(siteID)

  const { isLoading: minRLsFetchIsLoading, data: bulkHealthScores } =
    useFetchBulkHealthData(rolodexFetch)

  const aggregateData = useMemo(() => {
    if (rolodexFetch.isLoading) {
      return undefined
    }

    return rolodexFetch.allUnitsAtSite.map((curUnit) => {
      const componentsInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const inspectionsInUnit = filterEntriesWithTags(
        rolodexFetch.allInspectionsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const markupLayersInUnit = filterEntriesWithTags(
        rolodexFetch.allMarkupLayersAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const healthPlanActionsInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentHealthPlanActionsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const healthPlansInUnit =
        rolodexFetch.allComponentHealthPlansAtSite.filter((chp) => {
          return healthPlanActionsInUnit.some(
            (chpa) =>
              chpa.tags[RolodexEntryType.COMPONENT_HEALTH_PLAN] === chp.id
          )
        })

      const healthPlanActionSpecsInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentHealthPlanActionSpecsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const componentDatasetsInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentDatasetsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const componentDataFramesInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentDataFramesAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const componentDefaultsInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentDefaultsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const assetModel = rolodexFetch.allAssetModelsAtSite.find(
        (curAssetModel) =>
          curAssetModel.tags[RolodexEntryType.UNIT] === curUnit.id
      )

      const fdeCustomerUnit = rolodexFetch.allFDECustomerUnitsAtSite.find(
        (curFDECustomerUnit) =>
          curFDECustomerUnit.tags[RolodexEntryType.UNIT] === curUnit.id
      )

      const riskAssessmentForUnit = rolodexFetch.allRiskAssessmentsAtSite.find(
        (rA) => {
          return rA.tags[RolodexEntryType.UNIT] === curUnit.id
        }
      )

      const damageInfoForUnit = isNil(bulkHealthScores)
        ? undefined
        : componentsInUnit.reduce<
            ReadonlyArray<
              [
                string, // componentID
                DamageInfo,
              ]
            >
          >((acc, curComponent) => {
            const healthDataForComponent = bulkHealthScores[curComponent.id]
            if (isNil(healthDataForComponent)) {
              return acc
            }

            const preferredPlanId =
              healthPlansInUnit.find((hp) => hp.data.preferred)?.id ??
              DEFAULT_PLAN_KEY

            const planInfo = healthDataForComponent[preferredPlanId]

            if (isNil(planInfo) || isErrorInfo(planInfo)) {
              return acc
            }

            return [...acc, [curComponent.id, planInfo]]
          }, [])

      const minRlsForUnit = damageInfoForUnit?.reduce<{
        [componentId: string]: {
          [year: number]:
            | Omit<NonNullable<AggregateUnitData['health'][number]>, 'score'>
            | undefined
        }
      }>((agg, [curComponentId, curDamageInfo]) => {
        if (agg[curComponentId]) {
          throw new Error(`Duplicate component ID found: ${curComponentId}`)
        }

        const yearlyInfo = Object.entries(curDamageInfo).reduce<{
          [year: number]:
            | Omit<NonNullable<AggregateUnitData['health'][number]>, 'score'>
            | undefined
        }>((yearAgg, [year, yearData]) => {
          const yearAsNumber = parseInt(year)

          if (isNaN(yearAsNumber)) {
            throw new Error(`Invalid year: ${year}`)
          }

          yearAgg[yearAsNumber] = {
            riskScore: yearData.riskScore,
            minRLFromYear: yearData.minRl,
            percentBelowTMin: yearData.percentBelowTmin,
            damageAreas: {
              [curComponentId]: {
                riskScore: yearData.riskScore,
                healthScore: yearData.healthScore,
                damageAreas: yearData.damageAreas,
              },
            },
          }
          return yearAgg
        }, {})

        return { ...agg, [curComponentId]: yearlyInfo }
      }, {})

      return assembleUnitData({
        unit: curUnit,
        siteConfig: rolodexFetch.siteConfig,
        componentsInUnit,
        inspectionsInUnit,
        markupLayersInUnit,
        healthPlansInUnit: healthPlansInUnit,
        healthPlanActionsInUnit: healthPlanActionsInUnit,
        healthPlanActionSpecsInUnit: healthPlanActionSpecsInUnit,
        componentDatasetsInUnit,
        componentDataFramesInUnit,
        componentDefaultsInUnit,
        assetModel,
        fdeCustomerUnit,
        minRLsData: minRlsForUnit,
        riskAssessmentForUnit,
      })
    })
  }, [rolodexFetch, bulkHealthScores])

  return useMemo(() => {
    if (rolodexFetch.isLoading) {
      return {
        isMainLoading: true,
        aggregateSiteData: undefined,
        isRlCalcLoading: minRLsFetchIsLoading,
      }
    }

    return {
      isMainLoading: false,
      aggregateSiteData: aggregateData ?? [],
      isRlCalcLoading: minRLsFetchIsLoading,
    }
  }, [rolodexFetch, aggregateData, minRLsFetchIsLoading])
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/** Calculates and organizes all the asset-health-planning costs for a given Unit, broken down by Component */
function calculateAllPlanCostsInUnit(props: {
  componentsInUnit: ReadonlyArray<GeckoComponent>
  markupLayersInUnit: ReadonlyArray<GeckoMarkupLayer>
  healthPlansInUnit: ReadonlyArray<GeckoEntryBase<AhpPlan>>
  healthPlanActionsInUnit: ReadonlyArray<GeckoEntryBase<AhpAction>>
  healthPlanActionSpecsInUnit: ReadonlyArray<GeckoEntry>
  assetModel: GeckoAssetModel | undefined
  fdeCustomerUnit: GeckoEntryBase<FDECustomerUnit> | undefined
}): ReadonlyArray<AhpPlanActionAndCost> {
  const {
    componentsInUnit,
    markupLayersInUnit,
    healthPlansInUnit,
    healthPlanActionsInUnit,
    healthPlanActionSpecsInUnit,
    assetModel,
    fdeCustomerUnit,
  } = props

  const preferredHealthPlans = healthPlansInUnit.filter(
    (hp) => hp.data.preferred
  )

  return preferredHealthPlans.flatMap((curPlan) => {
    const planActions = filterEntriesWithTags(
      healthPlanActionsInUnit,
      RolodexEntryType.COMPONENT_HEALTH_PLAN,
      curPlan.id
    )

    return planActions.map((curAction) => {
      const actionSpec = healthPlanActionSpecsInUnit.find(
        (curSpec) => curAction.tags[curSpec.type] === curSpec.id
      )

      const actionComponent = componentsInUnit.find(
        ({ id }) => curAction.tags[RolodexEntryType.COMPONENT] === id
      )

      const planActionData = getHealthPlanActionData({
        ahpAction: curAction,
        linkedEntry: actionSpec,
        markupLayers: markupLayersInUnit,
        selectedComponent: actionComponent,
        selectedComponentModel: actionComponent
          ? assetModel?.data.components.get(actionComponent.id)
          : undefined,
        fdeCustomerUnit: fdeCustomerUnit,
      })

      return {
        plan: curPlan,
        action: curAction,
        cost: planActionData.costTotal,
      }
    })
  })
}

// --------------------------------------------------------------------------------------------------------------------
// Utility Functions
/** Assembles all the site-view-data for a unit into an easily digestible format */
function assembleUnitData(props: {
  unit: GeckoUnit
  siteConfig: GeckoSiteConfig | undefined
  componentsInUnit: ReadonlyArray<GeckoComponent>
  inspectionsInUnit: ReadonlyArray<GeckoInspection>
  componentDatasetsInUnit: ReadonlyArray<GeckoComponentDataset>
  componentDataFramesInUnit: ReadonlyArray<GeckoComponentDataFrame>
  componentDefaultsInUnit: ReadonlyArray<GeckoComponentDefaults>
  markupLayersInUnit: ReadonlyArray<GeckoMarkupLayer>
  healthPlansInUnit: ReadonlyArray<GeckoEntryBase<AhpPlan>>
  healthPlanActionsInUnit: ReadonlyArray<GeckoEntryBase<AhpAction>>
  healthPlanActionSpecsInUnit: ReadonlyArray<GeckoEntry>
  assetModel: GeckoAssetModel | undefined
  fdeCustomerUnit: GeckoEntryBase<FDECustomerUnit> | undefined
  riskAssessmentForUnit: GeckoRiskAssessment | undefined
  minRLsData:
    | {
        [componentID: string]: {
          [year: number]:
            | Omit<NonNullable<AggregateUnitData['health'][number]>, 'score'>
            | undefined
        }
      }
    | undefined
}): AggregateUnitData {
  const {
    unit,
    siteConfig,
    inspectionsInUnit,
    componentDatasetsInUnit,
    componentDataFramesInUnit,
    componentDefaultsInUnit,
    assetModel,
    fdeCustomerUnit,
    componentsInUnit,
    markupLayersInUnit,
    healthPlansInUnit,
    healthPlanActionsInUnit,
    healthPlanActionSpecsInUnit,
    minRLsData,
    riskAssessmentForUnit,
  } = props

  // - - - -
  // Most recent inspection

  const mostRecentInspection = inspectionsInUnit
    .toSorted((a, b) => {
      return a.data.date < b.data.date ? 1 : -1
    })
    .at(0)

  const validCDSs = componentDatasetsInUnit.filter(
    (cds) => cds.data.publishing_state === GeckoPublishingState.Published
  )

  const relevantCDF = componentDataFramesInUnit
    .toSorted(GeckoComponentDataFrame.GetDefaultSort(validCDSs))
    .find(
      (cdf) =>
        // Find a CDF matching this unit and inspection
        cdf.tags.unit === unit.id &&
        cdf.tags.inspection === mostRecentInspection?.id
    )

  const relevantComponent = componentsInUnit.find(
    (c) => c.id === relevantCDF?.tags.component
  )

  const relevantColorThreshold = componentDefaultsInUnit
    .find((cd) => cd.tags.component === relevantComponent?.id)
    ?.data.color_scale_thresholds.get(relevantCDF?.data.params.view.id ?? '')

  // - - -
  // Plan Costs

  const allPlanCosts = calculateAllPlanCostsInUnit({
    componentsInUnit,
    markupLayersInUnit,
    healthPlansInUnit,
    healthPlanActionsInUnit,
    healthPlanActionSpecsInUnit,
    assetModel,
    fdeCustomerUnit,
  })

  // - - -
  // Health Score

  const criticality = unit.data.custom_fields?.find(
    (cf) => cf.fieldName === 'criticality'
  )?.inputValue

  // For each year, get the minimum RL across all components (with ties broken by the component with the highest percent
  // below Tmin), and aggregate the damage areas
  const healthStatsBase = !isEmpty(minRLsData)
    ? Object.values(minRLsData)
    : listYearsToSimTo().map((year) => ({ [year]: undefined }))
  const minHealthStats = healthStatsBase.reduce<AggregateUnitData['health']>(
    (aggStats, componentYearlyData) => {
      Object.entries(componentYearlyData).forEach(([year, yearData]) => {
        const yearAsNumber = parseInt(year)
        if (isNaN(yearAsNumber)) {
          throw new Error(`Invalid year: ${year}`)
        }

        if (isNil(yearData)) {
          if (!isNil(riskAssessmentForUnit)) {
            // if there is no health-data for this year, use the risk assessment data
            aggStats[yearAsNumber] = {
              riskScore: getRiskScoreFromRiskClass(
                riskAssessmentForUnit.data.current_risk
              ),
              minRLFromYear: undefined,
              percentBelowTMin: undefined,
              damageAreas: aggStats[yearAsNumber]?.damageAreas ?? {},
            }
          }
          return
        }

        const yearDataToUse = {
          ...yearData,
          riskScore: riskAssessmentForUnit
            ? getRiskScoreFromRiskClass(riskAssessmentForUnit.data.current_risk)
            : undefined,
        }

        if (isNil(aggStats[yearAsNumber])) {
          aggStats[yearAsNumber] = {
            ...yearDataToUse,
          }
          return
        }

        const comparator = compareHealthData(
          aggStats[yearAsNumber],
          yearDataToUse,
          SortingPlacement.After
        )

        if (comparator < 0) {
          // the current aggStats has a lower RL, so we keep it but add the damage areas
          aggStats[yearAsNumber]['damageAreas'] = {
            ...aggStats[yearAsNumber]['damageAreas'],
            ...yearData.damageAreas,
          }
          return
        }

        // the current yearData has a lower RL, so we replace the aggStats with it but keep the damage areas
        aggStats[yearAsNumber] = {
          ...yearDataToUse,
          damageAreas: {
            ...aggStats[yearAsNumber]['damageAreas'],
            ...yearDataToUse.damageAreas,
          },
        }
      })

      return aggStats
    },
    {}
  )

  const mappedCriticality = getMappedCriticality(
    criticality,
    siteConfig?.data.site_level_criticality_mapping
  )

  return {
    unitSpecs: {
      unitData: unit,
      assetModel: assetModel,
      childComponents: componentsInUnit,
      mappedCriticality,
    },
    mostRecentInspectionData: {
      inspection: mostRecentInspection,
      relevantCDF: relevantCDF,
      relevantColorThreshold,
    },
    allPlanCosts: allPlanCosts,
    health: minHealthStats,
  }
}
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/** filter a set of entries by a given tag */
function filterEntriesWithTags<T extends { tags: Record<string, unknown> }>(
  entries: ReadonlyArray<T>,
  tagKey: string,
  valueToCompare: string | undefined
): ReadonlyArray<T> {
  return entries.filter((curEntry) => curEntry.tags[tagKey] === valueToCompare)
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * Get the site-view health score for a given min-RL and inspection status.
 */
export function getExtendedHealthScore(
  minRL: number | undefined,
  riskScore: number | undefined,
  hasInspection: boolean
): SiteViewHealthScore {
  // Risk score as assessed by users should take precedence over minRL
  if (!isNil(riskScore)) {
    return getRiskScoreHealthCondition(riskScore)
  }
  if (!isNil(minRL)) {
    return getComponentHealthCondition(minRL)
  }

  return hasInspection
    ? NoRLHealthScore.NotEnoughData
    : NoRLHealthScore.NoInspection
}

/**
 *  Compares two health data objects.
 *  A return of < 0 means the first object has a lower RL than the second
 *  If RLs are equal, the object with the higher percentBelowTMin is considered "worse" and placed before
 */
export function compareHealthData(
  a: {
    minRLFromYear: number | undefined
    percentBelowTMin: number | undefined
  },
  b: {
    minRLFromYear: number | undefined
    percentBelowTMin: number | undefined
  },
  placeUndefinedMinRl: SortingPlacement
): number {
  // If both have a minRLFromToday, compare them and break ties with percentBelowTMin
  if (!isNil(a.minRLFromYear) && !isNil(b.minRLFromYear)) {
    if (Math.max(a.minRLFromYear, 0) !== Math.max(b.minRLFromYear, 0)) {
      return a.minRLFromYear - b.minRLFromYear
    }
    return compareOptionalNumbers(
      b.percentBelowTMin,
      a.percentBelowTMin,
      SortingPlacement.Before
    )
  }

  // If only one has a minRLFromToday, usePlaceUndefined
  if (isNil(a.minRLFromYear) !== isNil(b.minRLFromYear)) {
    return isNil(a.minRLFromYear) ? placeUndefinedMinRl : -placeUndefinedMinRl
  }

  // If neither have a minRLFromToday, compare percentBelowTMin
  return compareOptionalNumbers(
    b.percentBelowTMin,
    a.percentBelowTMin,
    SortingPlacement.Before
  )
}
